// src/components/AuthRedirectHandler.js
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { showToast, WEBSERVICE, BITOHUBWEBSERVICE } from '../CoreDataService';
import { useBroker } from '../contexts/BrokerContext';
import axios from 'axios';
import CryptoJS from "crypto-js";

const LOGIN_REDIRECT_URL = 'https://myaccount.paybito.com/signin?continue=https%3A%2F%2Fad-center.bitocircle.com%2F&app=AdCenter';
const SECRETKEY = process.env.REACT_APP_SECRET_KEY;

const TOKEN_COOKIE_NAME = 'paybito_launch_platform_token';
const USERNAME_COOKIE_NAME = 'paybito_launch_platform_username';

// ========================================
// Cookie helpers for access token
// ========================================
const tokenCookie = {
  set: (token, days = 7) => {
    if (typeof document === 'undefined') return;
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${TOKEN_COOKIE_NAME}=${encodeURIComponent(token)}; expires=${expires}; path=/; domain=.bitocircle.com; Secure; SameSite=Lax`;
  },
  get: () => {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp(`(?:^|; )${TOKEN_COOKIE_NAME}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  },
  remove: () => {
    if (typeof document === 'undefined') return;
    document.cookie = `${TOKEN_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.bitocircle.com;`;
  },
};

// ========================================
// Cookie helpers for admin username
// ========================================
const usernameCookie = {
  set: (username, days = 7) => {
    if (typeof document === 'undefined') return;
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${USERNAME_COOKIE_NAME}=${encodeURIComponent(username)}; expires=${expires}; path=/; domain=.bitocircle.com; Secure; SameSite=Lax`;
  },
  get: () => {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp(`(?:^|; )${USERNAME_COOKIE_NAME}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  },
  remove: () => {
    if (typeof document === 'undefined') return;
    document.cookie = `${USERNAME_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.bitocircle.com;`;
  },
};

// Direct cleanup without API call — used by 401 interceptor
const forceLogout = () => {
  tokenCookie.remove();
  usernameCookie.remove();
  localStorage.clear();
  const cookies = [
    "paybito_adcenter_token", "paybito_adcenter_broker_id",
    "paybito_adcenter_user_id", "paybito_adcenter_uuid"
  ];
  cookies.forEach(name => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.paybito.com;`;
  });
};

// Full logout with API call — used by logout buttons
const logoutUser = async () => {
  const adminUserName = usernameCookie.get() || localStorage.getItem('adminUserName') || '';
  const accessToken = tokenCookie.get();

  try {
    const response = await axios.post(
      `${WEBSERVICE}/adminAccess/LogoutAdmin`,
      { user_name: adminUserName },
      {
        headers: {
          'Content-Type': 'application/json',
          authorization: `bearer ${accessToken}`,
        },
      }
    );

    if (response.data?.error?.error_data === 0) {
      forceLogout();
      return true;
    }
    return false;
  } catch (error) {
    console.error('Logout API failed:', error);
    return false;
  }
};

export { tokenCookie, usernameCookie, forceLogout, logoutUser };

const AuthRedirectHandler = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { brokerDetails, updateBrokerDetails } = useBroker();
  const [brokerDetailsFetched, setBrokerDetailsFetched] = useState(false);
  const hasProcessedParams = useRef(false);

  // Global axios and fetch interceptors
  useEffect(() => {
    // 1. Axios request and response interceptors
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const storedAccessToken = tokenCookie.get() || localStorage.getItem('access_token_us');
        if (storedAccessToken && config.headers) {
          config.headers.authorization = `bearer ${storedAccessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response && error.response.status === 401) {
          forceLogout();
          window.location.href = LOGIN_REDIRECT_URL;
        }
        return Promise.reject(error);
      }
    );

    // 2. Global fetch interceptor (monkey-patch window.fetch)
    const originalFetch = window.fetch;
    window.fetch = async function (resource, options = {}) {
      const storedAccessToken = tokenCookie.get() || localStorage.getItem('access_token_us');
      if (storedAccessToken) {
        // Initialize headers if not present
        if (!options.headers) {
          options.headers = {};
        }

        // Check if authorization header is already present (case-insensitive)
        let hasAuthHeader = false;
        if (options.headers instanceof Headers) {
          hasAuthHeader = options.headers.has('authorization');
        } else if (Array.isArray(options.headers)) {
          hasAuthHeader = options.headers.some(([key]) => key.toLowerCase() === 'authorization');
        } else if (typeof options.headers === 'object') {
          hasAuthHeader = Object.keys(options.headers).some(key => key.toLowerCase() === 'authorization');
        }

        // Attach bearer token if no authorization header exists
        if (!hasAuthHeader) {
          if (options.headers instanceof Headers) {
            options.headers.set('Authorization', `bearer ${storedAccessToken}`);
          } else if (Array.isArray(options.headers)) {
            options.headers.push(['Authorization', `bearer ${storedAccessToken}`]);
          } else if (typeof options.headers === 'object') {
            options.headers['Authorization'] = `bearer ${storedAccessToken}`;
          }
        }
      }

      try {
        const response = await originalFetch(resource, options);
        if (response.status === 401) {
          forceLogout();
          window.location.href = LOGIN_REDIRECT_URL;
        }
        return response;
      } catch (error) {
        return Promise.reject(error);
      }
    };

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
      window.fetch = originalFetch;
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const app = params.get('app');
    const rawQuery = location.search;
    const appRaw = rawQuery.split("app=")[1];

    // 1. Process URL Parameters (Decryption)
    if (app && !hasProcessedParams.current) {
      try {
        localStorage.setItem('appData', app);
        localStorage.setItem('appDataRaw', appRaw);
          
        const bytes = CryptoJS.AES.decrypt(app, SECRETKEY);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        const BrokerDetails = JSON.parse(decrypted);

        const access_token = BrokerDetails.token || '';
        const brokerId = BrokerDetails.adminUsersResult.brokerId || '';
        const userId = BrokerDetails.adminUsersResult.user_id || '';
        const uuid = BrokerDetails.adminUsersResult.uuid || '';
        const adminUserName = BrokerDetails.adminUsersResult.user_name || '';

        // Store token in cookie
        if (access_token) {
          tokenCookie.set(access_token);
        }

        // Store username in cookie
        if (adminUserName) {
          usernameCookie.set(adminUserName);
          localStorage.setItem('adminUserName', adminUserName);
        }

        // Store other auth data in localStorage
        if (brokerId) localStorage.setItem('brokerId', brokerId);
        if (userId) localStorage.setItem('userId', userId);
        if (uuid) localStorage.setItem('uuid', uuid);

        hasProcessedParams.current = true;
        navigate(location.pathname, { replace: true });
        return;
      } catch (error) {
        console.error('Decryption error:', error);
      }
    }

    // 2. Fetch Data Sequence
    const storedAccessToken = tokenCookie.get();
    const storedBrokerId = localStorage.getItem('brokerId');

    if (storedAccessToken && storedBrokerId && !brokerDetailsFetched) {
      
      const fetchDataSequence = async () => {
        try {
          // STEP A: Fetch Main Broker Details
          const response = await axios.get(`${WEBSERVICE}/admin/getBrokerDetails?brokerId=${storedBrokerId}`, {
            headers: { authorization: `bearer ${storedAccessToken}` },
          });

          if (response.data.error && response.data.error.error_data !== 0) {
            showToast('error', response.data.error.error_msg || 'Failed to fetch broker details.');
          } else {
            const fullResponseData = response.data.brokerInfo;
            
            // Store necessary keys for the next API call
            localStorage.setItem('bitoConnectUserId', fullResponseData.bitoConnectUserId);
            localStorage.setItem('financeHubUuid', fullResponseData.financeHubUuid);
            
            // Update context
            updateBrokerDetails(fullResponseData);

            // STEP B: Fetch Profile Photo from Bitohub API
            if (fullResponseData.financeHubUuid) {
              await fetchProfilePhoto(localStorage.getItem('uuid'));
            }

            setBrokerDetailsFetched(true);
          }
        } catch (error) {
          console.error('API Sequence failed:', error);
        }
      };

      // Helper function for the specific profile API
      const fetchProfilePhoto = async (uuid) => {
        try {
          const profileResponse = await fetch(`https://institutional-bo.paybito.com:8443/BitohubService/profile/getProfileDetails`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ adminUser: uuid, pageId: '0' }),
          });

          const result = await profileResponse.json();

          if (result.success && result.data) {
            const photoUrl = result.data.profilePicture || '';
            const finalPhotoUrl = photoUrl ? `${photoUrl}${photoUrl.includes('?') ? '&' : '?'}t=${Date.now()}` : '';
            
            localStorage.setItem('profilePhoto', finalPhotoUrl);
            console.log('Profile Photo synchronized from Bitohub');
          }
        } catch (err) {
          console.error('Secondary profile fetch failed:', err);
        }
      };

      // 5 second delay as per original requirement
      const timer = setTimeout(fetchDataSequence, 5000);
      return () => clearTimeout(timer);

    } else if (!storedAccessToken && hasProcessedParams.current) {
      forceLogout();
      window.location.href = LOGIN_REDIRECT_URL;
    }

  }, [location.search, location.pathname, navigate, updateBrokerDetails, brokerDetailsFetched]);

  return <>{children}</>;
};

export default AuthRedirectHandler;